import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import OrderDetailsModal from './OrderDetailsModal';
import MenuAndBillModal from './MenuAndBillModal';
import OrderCard from '../cards/OrderCard';
import { useMenuData } from '../../hooks/useMenuData';
import { useUserPermissions, useVenueId } from '../../hooks/useUserData';
import { useAuth0 } from '@auth0/auth0-react';
import { createVenueOrder, transferVenueTable } from '../../utils/staffApiService';
import { useOrdersContext } from '../../context/OrdersContext';
import { useRequestsContext } from '../../context/RequestsContext';
import { useVenueFeedback } from '../../context/VenueFeedbackContext';
import history from '../../utils/history';
import {
  getPendingRequestCountForTable,
  getTransferDestinationState,
  getTransferableOrderCountForTable,
  isTransferSourceEmpty,
} from '../../utils/tableTransferState';
import '../../styles/TableOrdersModal.css';
import TableTransferModal from './TableTransferModal';

const STALE_MENU_ERROR_CODES = new Set(['ITEM_NOT_ORDERABLE', 'ITEM_UNAVAILABLE']);

const TableOrdersModal = ({ show, handleClose, selectedTable, tables, orders, floors }) => {
  const venueId = useVenueId();
  const userPermissions = useUserPermissions();
  const { getAccessTokenSilently } = useAuth0();
  const { allOrders, refreshOrders } = useOrdersContext();
  const { requests, refreshRequests } = useRequestsContext();
  const { pushFeedback } = useVenueFeedback();
  const menu = useMenuData(venueId);
  const canTransferTable = userPermissions.includes('edit_orders');
  const canAccessCashier = userPermissions.includes('take_payment');
  const sourceTableId = selectedTable?.id ?? null;
  const isViewOnlyTable = selectedTable?.interactionMode === 'view_only' || selectedTable?.serviceState !== 'active';

  const mergedItems = (orders || []).reduce((acc, order) => acc.concat(order.items), []);
  const transferableOrderCount = getTransferableOrderCountForTable(allOrders, sourceTableId);
  const pendingRequestCount = getPendingRequestCountForTable(requests, sourceTableId);
  const occupancySummary =
    isViewOnlyTable
      ? selectedTable?.statusLabel || 'Servis Dışı'
      : transferableOrderCount > 0
      ? 'Aktif servis devam ediyor'
      : pendingRequestCount > 0
        ? 'Bekleyen talep var'
        : 'Şu an açık hareket yok';
  const serviceStateNotice =
    selectedTable?.serviceState === 'reserved'
      ? 'Bu masa rezerve durumda.'
      : selectedTable?.serviceState === 'inactive'
        ? 'Bu masa servis dışı.'
        : '';

  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showMenuAndBill, setShowMenuAndBill] = useState(false);
  const [showTableTransfer, setShowTableTransfer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState('');

  const destinationStates = useMemo(() => {
    return (tables || []).reduce((acc, table) => {
      acc[table.id] = getTransferDestinationState(table, allOrders, requests);
      return acc;
    }, {});
  }, [allOrders, requests, tables]);

  useEffect(() => {
    if (!show) {
      setShowTableTransfer(false);
      setIsTransferring(false);
      setTransferError('');
    }
  }, [show]);

  if (!selectedTable || !tables || !floors) {
    return null;
  }

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleCloseOrderDetails = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
  };

  const handleShowMenuAndBill = () => {
    setShowMenuAndBill(true);
  };

  const handleCloseMenuAndBill = () => {
    setShowMenuAndBill(false);
  };

  const handleShowTableTransfer = () => {
    setTransferError('');
    if (isTransferSourceEmpty(allOrders, requests, sourceTableId)) {
      pushFeedback({
        type: 'warning',
        title: 'Taşıma yapılamadı',
        message: 'Taşınacak aktif sipariş veya bekleyen istek yok.',
      });
      return;
    }
    setShowTableTransfer(true);
  };

  const handleCloseTableTransfer = () => {
    setShowTableTransfer(false);
    setTransferError('');
  };

  const handleOpenCashier = () => {
    if (!selectedTable?.id) return;
    handleClose();
    history.push(`/cashier?tableId=${selectedTable.id}`);
  };

  const resolveTransferErrorMessage = (error) => {
    switch (error?.code) {
      case 'SAME_TABLE_TRANSFER':
        return 'Kaynak ve hedef masa aynı olamaz.';
      case 'SOURCE_TABLE_EMPTY':
        return 'Taşınacak aktif sipariş veya bekleyen istek yok.';
      case 'DESTINATION_OCCUPIED':
        return 'Seçilen hedef masada aktif sipariş veya bekleyen istek var.';
      case 'DESTINATION_TABLE_UNAVAILABLE':
        return 'Seçilen hedef masa şu anda servis için uygun değil.';
      case 'VENUE_SCOPE_MISMATCH':
        return 'Seçilen masa bu işletmeye ait değil.';
      default:
        return `Masa taşıma işlemi başarısız oldu: ${error?.message || 'Bilinmeyen hata'}`;
    }
  };

  const handleTableTransfer = async (destinationTableId) => {
    setIsTransferring(true);
    setTransferError('');

    try {
      const result = await transferVenueTable(
        venueId,
        sourceTableId,
        destinationTableId,
        getAccessTokenSilently
      );

      await Promise.all([
        refreshOrders ? refreshOrders() : Promise.resolve(),
        refreshRequests ? refreshRequests() : Promise.resolve(),
      ]);

      handleCloseTableTransfer();
      handleClose();
      pushFeedback({
        type: 'success',
        title: 'Masa taşındı',
        autoHideMs: 6000,
        message:
          `Aktarılan sipariş: ${result?.movedOrders ?? 0}\n` +
          `Aktarılan istek: ${result?.movedRequests ?? 0}\n\n` +
          'Bu masadan sonra verilecek yeni siparişler için misafirlerin yeni masanın QR kodunu okutması gerekir.',
      });
    } catch (error) {
      setTransferError(resolveTransferErrorMessage(error));
    } finally {
      setIsTransferring(false);
    }
  };

  const handleSaveOrder = async (newOrder) => {
    try {
      const updatedItems = newOrder.items.map((item) => (
        item.quantityChange !== undefined
          ? { ...item, quantity: item.quantityChange }
          : item
      ));

      const order = {
        tableId: selectedTable.id,
        items: updatedItems,
        status: newOrder.status || 'pending',
        notes: newOrder.notes,
      };

      await createVenueOrder(venueId, order, getAccessTokenSilently);
      await refreshOrders();
      pushFeedback({
        type: 'success',
        title: 'Sipariş kaydedildi',
        message: `${selectedTable.name} için yeni sipariş akışa eklendi.`,
      });
    } catch (error) {
      if (!STALE_MENU_ERROR_CODES.has(error?.code)) {
        pushFeedback({
          type: 'error',
          title: 'Sipariş kaydedilemedi',
          message: error?.message || 'Sipariş oluşturulurken bir hata oluştu.',
        });
      }
      throw error;
    }
  };

  return (
    <>
      <Modal
        show={show}
        centered
        onHide={handleClose}
        dialogClassName={showMenuAndBill || showOrderDetails ? 'table-orders-modal dimmed' : 'table-orders-modal'}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="table-orders-modal__title">{selectedTable?.name}</div>
            <div className="table-orders-modal__subtitle">{occupancySummary}</div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isViewOnlyTable ? (
            <Alert variant="warning">{serviceStateNotice}</Alert>
          ) : null}
          <div className="venue-modal-summary">
            <div className="venue-modal-summary__item">
              <span className="venue-modal-summary__label">Aktif sipariş</span>
              <span className="venue-modal-summary__value">{transferableOrderCount}</span>
            </div>
            <div className="venue-modal-summary__item">
              <span className="venue-modal-summary__label">Bekleyen talep</span>
              <span className="venue-modal-summary__value">{pendingRequestCount}</span>
            </div>
            <div className="venue-modal-summary__item">
              <span className="venue-modal-summary__label">Toplam ürün</span>
              <span className="venue-modal-summary__value">{mergedItems.length}</span>
            </div>
          </div>

          <div className="table-orders-list">
            {orders.length === 0 ? (
              <div className="venue-empty-state">
                <p className="venue-empty-state__title">Açık sipariş yok</p>
                <p className="venue-empty-state__text">Yeni sipariş veya hesap özeti işlemi için aksiyon çubuğunu kullanın.</p>
              </div>
            ) : (
              orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onClick={handleOrderClick}
                  showTable={false}
                  showStatus={true}
                />
              ))
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="venue-modal-actions">
            {canTransferTable ? (
              <Button variant="outline-secondary" onClick={handleShowTableTransfer} disabled={isViewOnlyTable}>
                Masa Taşı
              </Button>
            ) : null}
            <Button variant="primary" onClick={handleShowMenuAndBill} disabled={isViewOnlyTable}>
              + Yeni Sipariş
            </Button>
            <div className="venue-modal-actions__spacer" />
            {canAccessCashier ? (
              <Button variant="success" onClick={handleOpenCashier} disabled={isViewOnlyTable}>
                Kasaya Git
              </Button>
            ) : null}
          </div>
        </Modal.Footer>
      </Modal>

      <OrderDetailsModal
        show={showOrderDetails}
        handleClose={handleCloseOrderDetails}
        order={selectedOrder}
        isEditable={true}
      />

      <MenuAndBillModal
        show={showMenuAndBill}
        handleClose={handleCloseMenuAndBill}
        menu={menu}
        tableSummary={mergedItems}
        saveOrder={handleSaveOrder}
        contextType="NewOrder"
      />

      <TableTransferModal
        show={showTableTransfer}
        handleClose={handleCloseTableTransfer}
        sourceTable={selectedTable}
        tables={tables}
        onTransfer={handleTableTransfer}
        floors={floors}
        isSubmitting={isTransferring}
        errorMessage={transferError}
        destinationStates={destinationStates}
      />
    </>
  );
};

export default TableOrdersModal;
